<template>
  <!-- The content and its own skeleton are one element, so the end of loading
       patches the slot rather than remounting it: a remount unmounted every
       child started while the page loaded, which cancelled each query it had in
       flight and fetched it again. A flex column taking its parent's gap, so the
       parts a page stacks in here keep the spacing they would have outside it.
       Only a requested reload dims, or every background poll would flicker. -->
  <div
    v-if="drawsSlot"
    :class="[
      'flex flex-col [gap:inherit]',
      !contentLoading && dimmed ? 'opacity-60 transition-opacity' : 'transition-opacity',
    ]"
    :aria-busy="contentLoading || dimmed || undefined"
    :data-state="contentLoading ? 'loading' : 'ready'"
    data-slot="async-boundary"
    :data-placeholder="contentLoading ? '' : undefined"
  >
    <span v-if="contentLoading" class="sr-only">{{ t("msg.loading") }}</span>
    <Notice
      v-else-if="errorText"
      tone="bad"
      role="alert"
      class="mb-3"
      :title="errorTitle || t('components.async.loadFailed')"
      :description="errorText"
    >
      <template #action>
        <Button type="button" variant="outline" size="sm" @click="retry">{{ t("common.retry") }}</Button>
      </template>
    </Notice>
    <slot :data="contentLoading ? (placeholder as T) : query ? (query.data.value as T) : (undefined as T)" />
  </div>

  <template v-else-if="phase === 'loading'">
    <div
      aria-busy="true"
      data-state="loading"
      data-slot="async-boundary"
      data-placeholder
      :class="sections.length > 1 ? 'flex flex-col gap-5' : undefined"
    >
      <span class="sr-only">{{ t("msg.loading") }}</span>
      <template v-for="(section, index) in sections" :key="index">
        <div v-if="section.kind === 'facts'" class="grid gap-y-3" aria-hidden="true">
          <div v-for="n in section.count" :key="n" class="flex flex-col gap-1.5">
            <Skeleton class="h-3 w-20" />
            <Skeleton class="h-4 w-48" />
          </div>
        </div>
        <div
          v-else-if="section.kind === 'stats'"
          class="flex flex-wrap gap-x-10 gap-y-4 border-y py-4 in-data-[slot=tile]:border-y-0 in-data-[slot=tile]:py-0"
          aria-hidden="true"
        >
          <div v-for="n in section.count" :key="n" class="flex flex-col gap-1.5">
            <Skeleton class="h-7 w-12" />
            <Skeleton class="h-3 w-20" />
          </div>
        </div>
        <div v-else-if="section.kind === 'rows'" class="flex flex-col gap-2" aria-hidden="true">
          <Skeleton v-for="n in section.count" :key="n" class="h-9 w-full" />
        </div>
        <div v-else-if="section.kind === 'list'" class="flex flex-col gap-2" aria-hidden="true">
          <div v-for="n in section.count" :key="n" class="flex items-center gap-3 rounded-lg border p-4">
            <Skeleton class="size-7 shrink-0 rounded-md" />
            <div class="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton class="h-4 w-48 max-w-full" />
              <Skeleton class="h-3 w-32 max-w-full" />
            </div>
          </div>
        </div>
        <!-- A TileList's rows. -->
        <ul v-else-if="section.kind === 'lines'" class="flex flex-col gap-1.5" aria-hidden="true">
          <li v-for="n in section.count" :key="n" class="flex h-5 items-center justify-between gap-3">
            <Skeleton :class="['h-3.5', LINE_WIDTHS[(n - 1) % LINE_WIDTHS.length]]" />
            <Skeleton class="h-3 w-14 shrink-0" />
          </li>
        </ul>
        <CardGrid v-else-if="section.kind === 'gridCards'" aria-hidden="true">
          <GridCard v-for="n in section.count" :key="n">
            <div class="flex items-center justify-between gap-3">
              <div class="flex min-w-0 items-center gap-1.5">
                <Skeleton class="size-5 shrink-0" />
                <Skeleton class="h-4 w-24" />
              </div>
              <Skeleton class="h-4 w-16 shrink-0" />
            </div>
            <Skeleton class="h-3.5 w-48 max-w-full" />
            <template #footer><Skeleton class="h-8 w-16" /></template>
          </GridCard>
        </CardGrid>
        <div v-else-if="section.kind === 'cards'" class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
          <div v-for="n in section.count" :key="n" class="flex flex-col gap-2 rounded-lg border p-4">
            <Skeleton class="h-4 w-32 max-w-full" />
            <Skeleton class="h-3 w-48 max-w-full" />
            <Skeleton class="h-3 w-40 max-w-full" />
            <Skeleton class="mt-2 h-8 w-24" />
          </div>
        </div>
        <!-- Matches FieldRow's layout and the kit field's height. -->
        <div v-else-if="section.kind === 'form'" class="flex flex-col gap-5" aria-hidden="true">
          <div v-for="n in section.count" :key="n" class="@container/field">
            <div :class="['grid gap-2', formLabels === 'beside' ? '@lg/field:grid-cols-[12rem_minmax(0,1fr)]' : '']">
              <div :class="['flex items-center', formLabels === 'beside' ? '@lg/field:min-h-9' : '']">
                <Skeleton class="h-4 w-28" />
              </div>
              <Skeleton class="h-9 w-full" />
            </div>
          </div>
        </div>
        <Skeleton v-else-if="section.kind === 'button'" class="h-9 w-full" aria-hidden="true" />
        <ChartSkeleton
          v-else-if="section.kind === 'chart'"
          :kind="chartKind"
          :height="chartHeight"
          :legend="chartLegend"
          :fill="chartFill"
        />
        <div
          v-else-if="section.kind === 'log'"
          class="flex flex-col gap-2 rounded border bg-black/50 p-4"
          aria-hidden="true"
        >
          <Skeleton v-for="n in section.count" :key="n" :class="['h-3.5', LOG_WIDTHS[(n - 1) % LOG_WIDTHS.length]]" />
        </div>
        <Skeleton v-else :class="BLOCK_SIZE[blockHeight]" aria-hidden="true" />
      </template>
    </div>
  </template>

  <!-- Failed with nothing to show: what failed, in place of the content. -->
  <LoadError
    v-else-if="phase === 'error'"
    :title="errorTitle"
    :error="query?.error.value"
    data-state="error"
    @retry="retry"
  />

  <!-- Nothing there, or nothing matching: one sentence and the action that fixes it. -->
  <div
    v-else-if="phase === 'empty'"
    class="py-6"
    :data-state="filtered ? 'no-results' : 'empty'"
    data-slot="async-boundary"
  >
    <p class="text-body text-muted-foreground">
      <slot v-if="!filtered" name="empty">{{ emptyText || t("components.async.empty") }}</slot>
      <template v-else>{{ t("common.noSearchResults") }}</template>
    </p>
    <div v-if="filtered || $slots['empty-action']" class="mt-3">
      <Button v-if="filtered" type="button" variant="outline" size="sm" @click="emit('clear-filters')">
        {{ t("components.async.clearFilters") }}
      </Button>
      <slot v-else name="empty-action" />
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
// Loading, empty and error states for a block of fetched data. With `:query` the
// slot gets `{ data }`, always defined; without one, pass `loading`, `error` and `empty`.
import { computed, ref, watch } from "vue";

import CardGrid from "@/components/kit/arrange/CardGrid.vue";
import GridCard from "@/components/kit/arrange/GridCard.vue";
import LoadError from "@/components/kit/arrange/LoadError.vue";
import ChartSkeleton from "@/components/kit/chart/ChartSkeleton.vue";
import Notice from "@/components/kit/arrange/Notice.vue";
import {
  defaultIsEmpty,
  loadErrorText,
  loadPhaseOf,
  provideLoading,
  type LoadPhase,
  type LoadState,
} from "@/components/kit/loadState";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useStrongTypedI18n } from "@/i18n/useStrongTypedI18n";

export type AsyncSkeleton =
  | "rows"
  | "lines"
  | "list"
  | "facts"
  | "stats"
  | "block"
  | "form"
  | "button"
  | "cards"
  | "gridCards"
  | "chart"
  | "log"
  | "content";
type PresetSkeleton = Exclude<AsyncSkeleton, "content">;

const props = withDefaults(
  defineProps<{
    query?: LoadState<T> | null;
    isEmpty?: (data: T) => boolean;
    // Slot data while `skeleton="content"` is loading.
    placeholder?: T;
    loading?: boolean;
    // The server's message (renderTypedError / renderError), or null.
    error?: string | null;
    // What failed, as a sentence ("Couldn't load access history").
    errorTitle?: string;
    // There is nothing to show yet: no rows, or not loaded.
    empty?: boolean;
    // The emptiness is the filters' doing, so it offers Clear filters instead.
    filtered?: boolean;
    emptyText?: string;
    // One preset, or several stacked in order. "content" stands alone.
    skeleton?: AsyncSkeleton | PresetSkeleton[];
    // Per preset when several are given.
    count?: number | number[];
    // The KitForm's `labels`.
    formLabels?: "above" | "beside";
    // "tall" for a diagram, "square" for a QR code.
    blockHeight?: "default" | "tall" | "square";
    // The chart the `chart` skeleton stands in for: its marks, its height in
    // pixels, whether it has a legend, and whether it grows to fill a column.
    chartKind?: "area" | "bar";
    chartHeight?: number;
    chartLegend?: boolean;
    chartFill?: boolean;
  }>(),
  {
    query: null,
    isEmpty: defaultIsEmpty,
    placeholder: undefined,
    loading: false,
    error: null,
    errorTitle: "",
    empty: false,
    filtered: false,
    emptyText: "",
    skeleton: "block",
    count: 3,
    formLabels: "above",
    blockHeight: "default",
    chartKind: "bar",
    chartHeight: 192,
    chartLegend: false,
    chartFill: false,
  },
);

const emit = defineEmits<{ retry: []; "clear-filters": [] }>();

const { t } = useStrongTypedI18n();

const BLOCK_SIZE = {
  default: "h-32 w-full",
  tall: "h-72 w-full",
  square: "mx-auto size-50",
} as const;

const sections = computed(() => {
  const kinds = Array.isArray(props.skeleton) ? props.skeleton : [props.skeleton];
  return kinds.map((kind, index) => ({
    kind,
    count: Array.isArray(props.count) ? (props.count[index] ?? 3) : props.count,
  }));
});

const LINE_WIDTHS = ["w-2/3", "w-1/2", "w-3/4", "w-2/5", "w-3/5"];
const LOG_WIDTHS = ["w-3/4", "w-1/2", "w-5/6", "w-2/3", "w-11/12", "w-2/5"];

// Read off props.query each time, so a page that swaps queries is followed.
const phase = computed<LoadPhase>(() => {
  if (props.query) return loadPhaseOf(props.query, (data) => props.isEmpty(data));
  if (props.loading && props.empty) return "loading";
  if (props.error && props.empty) return "error";
  if (props.empty) return "empty";
  return "ready";
});

// A content skeleton is the page's own markup drawn while loading, so it and the
// ready content are the one branch that renders the slot.
const contentLoading = computed(() => props.skeleton === "content" && phase.value === "loading");
const drawsSlot = computed(() => phase.value === "ready" || contentLoading.value);

const errorText = computed(() => (props.query ? loadErrorText(props.query, t) || null : props.error));

// Dimmed only for a requested reload: placeholder data from a key change, or a Retry.
const retrying = ref(false);
const dimmed = computed(() => {
  const query = props.query;
  if (!query) return props.loading;
  return query.isPlaceholderData?.value === true || (retrying.value && query.isFetching.value);
});
watch(
  () => props.query?.isFetching.value,
  (fetching) => {
    if (!fetching) retrying.value = false;
  },
);

function retry() {
  emit("retry");
  if (props.query) {
    retrying.value = true;
    void props.query.refetch();
  }
}

provideLoading(() => contentLoading.value);

defineSlots<{
  default(props: { data: T }): unknown;
  empty?(): unknown;
  "empty-action"?(): unknown;
}>();
</script>
